import { useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { useProgressoes } from '@/hooks/useProgressoes';
import { useAppSettings } from '@/context/AppSettingsContext';
import {
  calcularPontosCiclo,
  calcularProgressao,
  calcularLimiarFuncionario,
  pontosParaAvaliacao,
  PONTOS_HISTORICO,
  temDezoitoAnosEm30Ago2023,
  PONTOS_REGRA_18_ANOS,
  type PontosEscalasConfig,
  type AvaliacaoSiadap,
} from '@/utils/progressaoUtils';

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

function calcularAnosServico(dataEntrada?: string): number | null {
  if (!dataEntrada) return null;
  const entrada = new Date(dataEntrada);
  if (isNaN(entrada.getTime())) return null;
  const hoje = new Date();
  let anos = hoje.getFullYear() - entrada.getFullYear();
  const mesAtual = hoje.getMonth();
  const mesEntrada = entrada.getMonth();
  if (mesAtual < mesEntrada || (mesAtual === mesEntrada && hoje.getDate() < entrada.getDate())) anos -= 1;
  return Math.max(0, anos);
}

const avaliacaoConfig: Record<string, { bg: string; text: string; dot: string; bar: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   bar: 'bg-amber-400'   },
  Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-400'    },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400',  bar: 'bg-orange-400'  },
  Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500',    bar: 'bg-rose-500'    },
  Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-400'    },
  Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500',    bar: 'bg-teal-500'    },
};

const avatarPalette = [
  { bg: 'bg-rose-100',   text: 'text-rose-600'   },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100',  text: 'text-amber-600'  },
  { bg: 'bg-teal-100',   text: 'text-teal-600'   },
  { bg: 'bg-cyan-100',   text: 'text-cyan-600'   },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getAvatarPalette(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarPalette.length;
  return avatarPalette[isNaN(idx) ? 0 : idx];
}

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('');
}

export default function RelatorioFuncionarioPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { funcionarios } = useFuncionarios();
  const { registosSiadap, registosHistorico, pontosDescontadosPorFuncionario } = useSiadap();
  const { settings } = useAppSettings();
  const printRef = useRef<HTMLDivElement>(null);

  const funcionario = useMemo(() => funcionarios.find((f) => f.id === id) ?? null, [funcionarios, id]);

  const { progressoes, loading: loadingProgressoes } = useProgressoes(id ?? null);

  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente: settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom,
      Regular: settings.pontos_regular,
      Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023,
      Regular: settings.pontos_regular_2023,
      Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente: settings.pontos_excelente_pre2023,
      Relevante: settings.pontos_relevante_pre2023,
      Adequado: settings.pontos_adequado_pre2023,
      Inadequado: settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom: settings.pontos_adequado_pre2023,
      Regular: settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente: settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom: settings.pontos_bom,
    Regular: settings.pontos_regular,
    Inadequado: settings.pontos_inadequado,
  }), [settings]);

  const siadapDoFunc = useMemo(() => registosSiadap.filter((r) => r.funcionarioId === id), [registosSiadap, id]);
  const historicoDoFunc = useMemo(() => registosHistorico.filter((r) => r.funcionarioId === id), [registosHistorico, id]);

  const pontosDescontados = useMemo(() => pontosDescontadosPorFuncionario[id ?? ''] ?? funcionario?.pontosDescontados ?? 0, [pontosDescontadosPorFuncionario, id, funcionario]);

  const isPrepav = funcionario?.excecaoPrepav === true;
  const temRegistos = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
  // Pontos manuais são SEMPRE somados (independentemente de PREPAV ou ter registos SIADAP)
  const pontosManuais = funcionario?.pontosAtuais ?? 0;

  const pontosTotal = useMemo(() => {
    if (!funcionario) return 0;
    return calcularPontosCiclo(siadapDoFunc, historicoDoFunc, pontosDescontados, pontosManuais, pontosMap, undefined, escalas);
  }, [funcionario, siadapDoFunc, historicoDoFunc, pontosDescontados, pontosManuais, pontosMap, escalas]);

  const limiar = useMemo(() => {
    if (!funcionario) return settings.pontos_progressao;
    const { limiar: l } = calcularLimiarFuncionario(
      siadapDoFunc,
      historicoDoFunc,
      settings.pontos_progressao,
      settings.pontos_progressao_pre2025,
      settings.pontos_progressao_2023,
      escalas
    );
    return l;
  }, [funcionario, siadapDoFunc, historicoDoFunc, settings, escalas]);

  // Regra especial: só mostra aviso se limiar normal > 6 (8 ou 10 pts) e tem 18+ anos em 30/08/2023
  const temRegraEspecial = limiar > PONTOS_REGRA_18_ANOS && temDezoitoAnosEm30Ago2023(funcionario?.dataEntradaFuncaoPublica);

  const progressaoInfo = useMemo(() => {
    if (!funcionario) return null;
    return calcularProgressao(pontosTotal, funcionario.nivelAtual, limiar);
  }, [funcionario, pontosTotal, limiar]);

  // Avaliações SIADAP ordenadas por ano desc
  const avaliacoesSiadap = useMemo(() => {
    return [...siadapDoFunc].sort((a, b) => {
      const anoA = parseInt((a.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
      const anoB = parseInt((b.periodoAvaliacao ?? '').split('-').pop() ?? '0', 10);
      return anoB - anoA;
    });
  }, [siadapDoFunc]);

  // Histórico manual ordenado por ano desc
  const avaliacoesHistorico = useMemo(() => {
    return [...historicoDoFunc].sort((a, b) => (b.ano ?? 0) - (a.ano ?? 0));
  }, [historicoDoFunc]);

  // Pontos por avaliação SIADAP
  const pontosSiadapPorRegisto = useMemo(() => {
    return avaliacoesSiadap.map((r) => ({
      ...r,
      pontos: pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas),
    }));
  }, [avaliacoesSiadap, pontosMap, escalas]);

  const totalPontosSiadap = pontosSiadapPorRegisto.reduce((s, r) => s + r.pontos, 0);
  const totalPontosHistorico = avaliacoesHistorico.reduce((s, r) => s + (PONTOS_HISTORICO[r.classificacaoServico] ?? 0), 0);
  const anosServico = calcularAnosServico(funcionario?.dataEntradaFuncaoPublica);

  function handlePrint() {
    window.print();
  }

  if (!funcionario) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 flex items-center justify-center bg-zinc-100 rounded-full mx-auto">
            <i className="ri-user-search-line text-3xl text-zinc-400" />
          </div>
          <p className="text-zinc-500 font-medium">Funcionário não encontrado</p>
          <button onClick={() => navigate('/funcionarios')} className="whitespace-nowrap text-sm text-zinc-400 hover:text-zinc-700 underline cursor-pointer">
            Voltar à lista
          </button>
        </div>
      </main>
    );
  }

  const palette = getAvatarPalette(funcionario.id);
  const initials = getInitials(funcionario.nome);

  return (
    <>
      <style>{`
        @media print {
          /* Esconder tudo excepto o conteúdo do relatório */
          .no-print { display: none !important; }

          /* Esconder sidebar, topheader e qualquer layout wrapper */
          nav, aside, header,
          [class*="sidebar"], [class*="Sidebar"],
          [class*="topheader"], [class*="TopHeader"],
          [class*="AppLayout"] > *:not(main) {
            display: none !important;
          }

          /* Reset global */
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
          }

          /* O main ocupa tudo */
          main {
            background: white !important;
            width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block !important;
          }

          /* Container do relatório */
          .print-container {
            max-width: 100% !important;
            width: 100% !important;
            padding: 8px 12px !important;
            margin: 0 !important;
            box-sizing: border-box !important;
          }

          /* Cada secção não parte a meio */
          .print-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Remover sombras e bordas desnecessárias */
          .print-area {
            box-shadow: none !important;
            border: 1px solid #e4e4e7 !important;
          }

          /* Tabelas não partem a meio */
          table { page-break-inside: avoid !important; break-inside: avoid !important; }

          /* Forçar cores a imprimir */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

          @page {
            margin: 1cm;
            size: A4 portrait;
          }
        }
      `}</style>

      <main className="flex-1 bg-slate-50/60">
        {/* PAGE HEADER */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5 no-print">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/funcionarios')}
                className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 hover:text-zinc-800 hover:border-zinc-300 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-base" />
              </button>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-file-chart-line text-white text-base" />
              </div>
              <div>
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Relatório Individual</p>
                <h1 className="text-[18px] font-bold text-zinc-900 leading-tight">{funcionario.nome}</h1>
              </div>
            </div>
            <button
              onClick={handlePrint}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line text-sm" /></div>
              Imprimir / PDF
            </button>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5 print-container" ref={printRef}>

          {/* CABEÇALHO DO RELATÓRIO */}
          <div className="bg-white rounded-xl border border-zinc-100 p-6 print-area print-section">
            <div className="flex items-start gap-5">
              <div className={`w-16 h-16 flex items-center justify-center rounded-full text-xl font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-1">Relatório Individual de Progressão</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-2xl font-bold text-zinc-900">{funcionario.nome}</h2>
                      {isPrepav && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                          <i className="ri-shield-star-line text-xs" />
                          PREPAV
                        </span>
                      )}
                      {temRegraEspecial && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full" title="Regra especial 2024 — 18+ anos de serviço a 30/08/2023">
                          <i className="ri-award-line text-xs" />
                          Regra especial 2024
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500 mt-1">{funcionario.carreira} · <span className="font-mono text-zinc-400">#{funcionario.id}</span></p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] text-zinc-400">Gerado em</p>
                    <p className="text-sm font-semibold text-zinc-700">{new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                    <p className="text-[11px] text-zinc-400 mt-1">{settings.nome_camara}</p>
                  </div>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-zinc-100">
                  {[
                    { label: 'Unidade Orgânica', value: funcionario.unidadeOrganica || '—' },
                    { label: 'Nível Atual', value: `Nível ${funcionario.nivelAtual}` },
                    { label: 'Vencimento', value: (funcionario.vencimento ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) },
                    { label: 'Anos de Serviço', value: anosServico !== null ? `${anosServico} anos` : '—' },
                  ].map((item) => (
                    <div key={item.label}>
                      <p className="text-[11px] text-zinc-400 font-medium uppercase tracking-wide">{item.label}</p>
                      <p className="text-sm font-semibold text-zinc-800 mt-0.5">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RESUMO DE PONTOS */}
          <div className="bg-white rounded-xl border border-zinc-100 p-6 print-area print-section">
            <h3 className="text-[13px] font-bold text-zinc-800 uppercase tracking-wide mb-4 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                <i className="ri-award-line text-base" />
              </div>
              Resumo de Pontos
            </h3>

            <div className={`grid gap-4 mb-5 ${isPrepav ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}>
              {[
                { label: 'Pontos SIADAP', value: totalPontosSiadap.toFixed(1), icon: 'ri-bar-chart-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                { label: 'Pontos Histórico', value: totalPontosHistorico.toFixed(1), icon: 'ri-history-line', color: 'text-amber-600', bg: 'bg-amber-50' },
                ...(isPrepav ? [{ label: 'Pontos PREPAV', value: (funcionario.pontosAtuais ?? 0).toFixed(1), icon: 'ri-shield-star-line', color: 'text-violet-600', bg: 'bg-violet-50' }] : []),
                { label: 'Pontos Descontados', value: `-${pontosDescontados.toFixed(1)}`, icon: 'ri-subtract-line', color: 'text-rose-600', bg: 'bg-rose-50' },
                { label: 'Total Líquido', value: pontosTotal.toFixed(1), icon: 'ri-coins-line', color: 'text-zinc-800', bg: 'bg-zinc-100' },
              ].map((s) => (
                <div key={s.label} className={`rounded-xl p-4 ${s.bg}`}>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-2 ${s.color} bg-white/60`}>
                    <i className={`${s.icon} text-base`} />
                  </div>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Nota PREPAV */}
            {isPrepav && (
              <div className="mb-4 flex items-start gap-2.5 bg-amber-50 border border-amber-100 rounded-xl p-3.5">
                <div className="w-5 h-5 flex items-center justify-center text-amber-500 flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-sm" />
                </div>
                <p className="text-[12px] text-amber-800 leading-relaxed">
                  <strong>Exceção PREPAV ativa:</strong> Os pontos PREPAV ({(funcionario.pontosAtuais ?? 0).toFixed(1)} pts) são somados aos pontos SIADAP e histórico para o cálculo do total líquido.
                </p>
              </div>
            )}

            {/* Barra de progressão */}
            {progressaoInfo && (
              <div className={`border rounded-xl p-4 ${temRegraEspecial ? 'border-orange-100 bg-orange-50/30' : 'border-zinc-100'}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-zinc-700">Progressão para Nível {progressaoInfo.proximoNivel}</span>
                    {progressaoInfo.status === 'pronto' && (
                      <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Elegível</span>
                    )}
                    {progressaoInfo.status === 'proximo' && (
                      <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Próximo</span>
                    )}
                    {temRegraEspecial && (
                      <span className="text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                        <i className="ri-award-line text-[10px]" />
                        Limiar especial: {PONTOS_REGRA_18_ANOS} pts
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-zinc-600">
                    {pontosTotal.toFixed(1)} / {limiar} pts ({progressaoInfo.percentagem.toFixed(0)}%)
                  </span>
                </div>
                <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      progressaoInfo.status === 'pronto' ? 'bg-emerald-500' :
                      progressaoInfo.status === 'proximo' ? 'bg-amber-400' : 'bg-zinc-400'
                    }`}
                    style={{ width: `${progressaoInfo.percentagem}%` }}
                  />
                </div>
                {progressaoInfo.status !== 'pronto' && (
                  <p className="text-[12px] text-zinc-400 mt-1.5">
                    Faltam <strong className="text-zinc-600">{progressaoInfo.pontosFaltam.toFixed(1)} pontos</strong> para atingir o limiar de progressão ({limiar} pts)
                  </p>
                )}
                {/* Nota da regra especial com data de referência explícita */}
                {temRegraEspecial && (
                  <div className="mt-3 flex items-start gap-2.5 bg-orange-50 border border-orange-100 rounded-lg p-3">
                    <div className="w-5 h-5 flex items-center justify-center text-orange-500 flex-shrink-0 mt-0.5">
                      <i className="ri-award-line text-sm" />
                    </div>
                    <div>
                      <p className="text-[12px] font-bold text-orange-800 mb-0.5">Regra especial 2024 — Art.º aplicável (LGTFP)</p>
                      <p className="text-[11px] text-orange-700 leading-relaxed">
                        Este funcionário tinha <strong>18 ou mais anos de serviço na função pública à data de referência legal: 30/08/2023</strong>.
                        O limiar de elegibilidade aplicável é de <strong>{PONTOS_REGRA_18_ANOS} pontos</strong> (em vez do limiar geral de {limiar === PONTOS_REGRA_18_ANOS ? '8' : limiar} pts).
                        Esta regra aplica-se apenas ao período de avaliação 2023–2024.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* HISTÓRICO SIADAP */}
          <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden print-area print-section">
            <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="text-[13px] font-bold text-zinc-800 uppercase tracking-wide flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-emerald-500">
                  <i className="ri-bar-chart-line text-base" />
                </div>
                Histórico SIADAP
                <span className="text-[11px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-semibold">{avaliacoesSiadap.length}</span>
              </h3>
              <span className="text-[12px] text-zinc-400">
                Total: <strong className="text-emerald-600">{totalPontosSiadap.toFixed(1)} pts</strong>
              </span>
            </div>

            {avaliacoesSiadap.length === 0 ? (
              <div className="py-10 flex flex-col items-center gap-2 text-zinc-400">
                <div className="w-10 h-10 flex items-center justify-center"><i className="ri-bar-chart-line text-3xl" /></div>
                <p className="text-sm">Sem avaliações SIADAP registadas</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-100">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Período</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Avaliação</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontuação Chefe</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontuação CCA</th>
                    <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontos</th>
                  </tr>
                </thead>
                <tbody>
                  {pontosSiadapPorRegisto.map((r, i) => {
                    const cfg = avaliacaoConfig[r.avaliacao] ?? avaliacaoConfig['Adequado'];
                    return (
                      <tr key={r.id} className={`border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/30'}`}>
                        <td className="px-5 py-3">
                          <span className="text-sm font-semibold text-zinc-700">{r.periodoAvaliacao || '—'}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold ${cfg.bg} ${cfg.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                            {r.avaliacao}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                              <div className="h-full bg-zinc-400 rounded-full" style={{ width: `${(r.pontosChefe / 5) * 100}%` }} />
                            </div>
                            <span className="text-sm text-zinc-600 font-medium">{r.pontosChefe.toFixed(1)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(r.pontosCCA / 5) * 100}%` }} />
                            </div>
                            <span className="text-sm text-zinc-600 font-medium">{r.pontosCCA.toFixed(1)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className={`text-sm font-bold ${cfg.text}`}>
                            {r.pontos % 1 === 0 ? r.pontos.toFixed(0) : r.pontos.toFixed(1)} pts
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-emerald-50/60 border-t-2 border-emerald-100">
                    <td colSpan={4} className="px-5 py-3 text-[12px] font-semibold text-zinc-500">
                      Total SIADAP ({avaliacoesSiadap.length} avaliações)
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="text-sm font-bold text-emerald-700">
                        {totalPontosSiadap % 1 === 0 ? totalPontosSiadap.toFixed(0) : totalPontosSiadap.toFixed(1)} pts
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* HISTÓRICO MANUAL (pré-SIADAP) */}
          {avaliacoesHistorico.length > 0 && (
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden print-area print-section">
              <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-[13px] font-bold text-zinc-800 uppercase tracking-wide flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-500">
                    <i className="ri-history-line text-base" />
                  </div>
                  Histórico Manual (Pré-SIADAP)
                  <span className="text-[11px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-semibold">{avaliacoesHistorico.length}</span>
                </h3>
                <span className="text-[12px] text-zinc-400">
                  Total: <strong className="text-amber-600">{totalPontosHistorico.toFixed(1)} pts</strong>
                </span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-100">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Ano</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Classificação</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontuação Chefe</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontuação CCA</th>
                    <th className="px-5 py-3 text-right text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontos</th>
                  </tr>
                </thead>
                <tbody>
                  {avaliacoesHistorico.map((r, i) => {
                    const pts = PONTOS_HISTORICO[r.classificacaoServico] ?? 0;
                    const cfg = avaliacaoConfig[r.classificacaoServico] ?? avaliacaoConfig['Adequado'];
                    return (
                      <tr key={r.id} className={`border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/30'}`}>
                        <td className="px-5 py-3">
                          <span className="text-sm font-semibold text-zinc-700">{r.ano}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold ${cfg.bg} ${cfg.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                            {r.classificacaoServico}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                              <div className="h-full bg-zinc-400 rounded-full" style={{ width: `${(r.pontuacaoChefe / 5) * 100}%` }} />
                            </div>
                            <span className="text-sm text-zinc-600 font-medium">{r.pontuacaoChefe.toFixed(1)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(r.pontuacaoCCA / 5) * 100}%` }} />
                            </div>
                            <span className="text-sm text-zinc-600 font-medium">{r.pontuacaoCCA.toFixed(1)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-sm font-bold text-amber-700">
                            {pts % 1 === 0 ? pts.toFixed(0) : pts.toFixed(1)} pts
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-50/60 border-t-2 border-amber-100">
                    <td colSpan={4} className="px-5 py-3 text-[12px] font-semibold text-zinc-500">
                      Total Histórico ({avaliacoesHistorico.length} registos)
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="text-sm font-bold text-amber-700">
                        {totalPontosHistorico % 1 === 0 ? totalPontosHistorico.toFixed(0) : totalPontosHistorico.toFixed(1)} pts
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* HISTÓRICO DE PROGRESSÕES */}
          <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden print-area print-section">
            <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="text-[13px] font-bold text-zinc-800 uppercase tracking-wide flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-emerald-500">
                  <i className="ri-arrow-up-circle-line text-base" />
                </div>
                Histórico de Progressões
                <span className="text-[11px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-semibold">{progressoes.length}</span>
              </h3>
              {progressoes.length > 0 && (
                <span className="text-[12px] text-zinc-400">
                  Total descontado: <strong className="text-rose-600">{pontosDescontados.toFixed(1)} pts</strong>
                </span>
              )}
            </div>

            {loadingProgressoes ? (
              <div className="py-10 flex flex-col items-center gap-2 text-zinc-400">
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-2xl animate-spin" /></div>
                <p className="text-sm">A carregar...</p>
              </div>
            ) : progressoes.length === 0 ? (
              <div className="py-10 flex flex-col items-center gap-2 text-zinc-400">
                <div className="w-10 h-10 flex items-center justify-center"><i className="ri-arrow-up-circle-line text-3xl" /></div>
                <p className="text-sm">Sem progressões registadas</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-100">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Nº</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Data</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Progressão de Nível</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Pontos Descontados</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Acelerador</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {[...progressoes].reverse().map((p, i) => (
                    <tr key={p.id} className={`border-b border-zinc-50 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/30'}`}>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${i === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                          {progressoes.length - i}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-zinc-700 font-medium">{formatDate(p.dataProgressao)}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-zinc-500">Nível {p.nivelAnterior}</span>
                          <div className="w-4 h-4 flex items-center justify-center text-emerald-500">
                            <i className="ri-arrow-right-line text-sm" />
                          </div>
                          <span className="text-sm font-bold text-emerald-700">Nível {p.nivelNovo}</span>
                          <span className="text-[11px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                            +{p.nivelNovo - p.nivelAnterior}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1 text-sm font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg">
                          <i className="ri-subtract-line text-xs" />
                          {p.pontosDescontados % 1 === 0 ? p.pontosDescontados.toFixed(0) : p.pontosDescontados.toFixed(1)} pts
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {p.aceleradorRemuneratorio ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            <i className="ri-flashlight-line text-[10px]" />
                            Sim
                          </span>
                        ) : (
                          <span className="text-sm text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-zinc-500 italic">{p.observacoes || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-rose-50/60 border-t-2 border-rose-100">
                    <td colSpan={3} className="px-5 py-3 text-[12px] font-semibold text-zinc-500">
                      Total ({progressoes.length} progressão(ões)) · Nível {progressoes[0].nivelAnterior} → {progressoes[progressoes.length - 1].nivelNovo}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-sm font-bold text-rose-700 bg-rose-100 px-2.5 py-1 rounded-lg">
                        <i className="ri-subtract-line text-xs" />
                        {pontosDescontados % 1 === 0 ? pontosDescontados.toFixed(0) : pontosDescontados.toFixed(1)} pts
                      </span>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* RODAPÉ */}
          <div className="text-center py-4 text-[11px] text-zinc-400 print-area print-section">
            <p>{settings.nome_camara} · SIADAP para RH · Ciclo {settings.ciclo_siadap}</p>
            <p className="mt-0.5">Documento gerado automaticamente em {new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
          </div>

        </div>
      </main>
    </>
  );
}
